import React from 'react';
import classNames from 'classnames';
import { Divider } from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { NAV_COMPONENT, SuiteId, TRACKER, URL_PARAMETER } from '~/constants';
import {
    assign,
    compact,
    find,
    forEach,
    get,
    getUrlSearchParams,
    isEmpty,
    isNil,
    join,
    map,
    size,
    tracker
} from '~/utils';
import { useStores } from '~/hooks';
import Browser from './components/browser';
import Profile, { ProfileMenuType } from './components/profile';
import Contacts from './components/contacts';
import { useNavItemShow } from './hooks';
import { NavMessage, ProductMenu } from '@fx-ui/platform-sdk';

interface INavigationBarProps {
    className?: string;
    style?: React.CSSProperties;
    navCenter?: React.ReactNode;
    left?: NAV_COMPONENT[][];
    leftEl?: React.ReactNode;
    /**
     * 如果若干个导航项是一组的，则将它们放在同一个数组内
     * 例如[[a], [b,c], [d]]，则a为一组，bc为一组，d为一组
     */
    right?: NAV_COMPONENT[][];
    rightEl?: React.ReactNode;
    rightPlugin?: React.ReactNode;
    defaultMenu?: string;
    profileItems?: ProfileMenuType[];
}

const NavigationBar: React.FC<INavigationBarProps> = (props) => {
    const {
        className,
        style,
        navCenter,
        defaultMenu = 'dashboard',
        left = [[NAV_COMPONENT.MENU]],
        leftEl,
        right = [[NAV_COMPONENT.BROWSER, NAV_COMPONENT.MESSAGE, NAV_COMPONENT.PROFILE]],
        rightPlugin,
        profileItems,
        rightEl
    } = props;

    const { corpUserStore } = useStores();

    const { isNavCompShow } = useNavItemShow();

    const cls = classNames('fx-navigation-bar', className);

    /**
     * 创建导航栏组件，支持分组显示， 每组后面会跟着一个分割线
     * @see https://fanruan-x.feishu.cn/wiki/H1m8wAXr7iaqTIkZaGjc33MKn80
     * @param components 导航栏组件列表
     */
    const createComponents = (components?: NAV_COMPONENT[][]) => {
        return (
            <>
                {map(components, (comp, index) => {
                    const navGroup = map(comp, (c) => {
                        const navComponent = createComp(c);
                        // 只渲染有效的 React 元素
                        if (React.isValidElement(navComponent)) {
                            return (
                                <div className={classNames('comp-item', c)} key={c}>
                                    {navComponent}
                                </div>
                            );
                        }
                        return null;
                    });

                    // 如果这组没有有效的组件则不渲染
                    if (isEmpty(compact(navGroup))) {
                        return null;
                    }

                    // 最后一组导航栏组件不显示右边的分割线
                    const isLastGroup = size(components) === index + 1;

                    return (
                        <div className="nav-group" key={join(comp, '-')}>
                            {navGroup}
                            {!isLastGroup && (
                                <Divider direction="vertical" className="nav-divider" />
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    const createComp = (compName: NAV_COMPONENT) => {
        switch (compName) {
            case NAV_COMPONENT.MENU:
                return createMenuComp(true);
            case NAV_COMPONENT.DOCK:
                return createMenuComp(false);
            case NAV_COMPONENT.BROWSER:
                return <Browser />;
            case NAV_COMPONENT.MESSAGE: {
                // 获取当前是哪个产品
                const suiteId = (() => {
                    // 优先从页面中的 _from 参数取
                    const suiteIdParam = getUrlSearchParams(URL_PARAMETER._FROM);
                    if (suiteIdParam) {
                        return parseInt(suiteIdParam, 10);
                    }
                    // 否则尝试取用户最近一次访问的子产品
                    if (!isNil(corpUserStore.latestAccessSuiteId)) {
                        return corpUserStore.latestAccessSuiteId;
                    }
                    // 否则就试着从 login_user_info.suiteModules 里找第一个开启了的子产品
                    const defaultSuiteId = find(
                        corpUserStore.suiteModules,
                        (item) => item!.enable
                    )?.suiteId;
                    if (defaultSuiteId) {
                        return defaultSuiteId;
                    }
                    // 否则就认为当前不在某个具体子产品中了，直接认为是平台环境了
                    return SuiteId.PLATFORM;
                })();
                return <NavMessage suiteId={suiteId} />;
            }
            case NAV_COMPONENT.PROFILE:
                return <Profile items={profileItems} />;
            case NAV_COMPONENT.CONTACTS:
                return <Contacts />;
            default:
                break;
        }
    };

    /**
     * 切换知识管理/工作台/通讯录/知识库/开放平台/方案中心的Menu
     *
     * @param hasLink
     */
    const createMenuComp = (hasLink: boolean) => {
        const { name, pc_icon_url, url } = getItemInfo(defaultMenu) || {};
        const menuProps = {};

        if (defaultMenu === 'dashboard') {
            assign(menuProps, {
                handleMenuClick: () => {
                    tracker(TRACKER.NAV_TOOLBAR_CLICK, {
                        area: '',
                        type: defaultMenu
                    });
                }
            });
        }

        return (
            <div className="menu-wrapper">
                <ProductMenu
                    onTriggerClick={() => {
                        tracker(TRACKER.NAV_TOOLBAR_CLICK, {
                            area: '',
                            type: 'dock_menu'
                        });
                        tracker(TRACKER.VIEW_DOCK_MENU);
                    }}
                    onSwitchCorpClick={() => {
                        tracker(TRACKER.DOCK_MENU_BUTTON_CLICK, {
                            area: '',
                            type: 'corp_switch'
                        });
                    }}
                    onMenuClick={(menuKey) => {
                        tracker(TRACKER.DOCK_MENU_BUTTON_CLICK, {
                            area: '',
                            type: menuKey
                        });
                        switch (menuKey) {
                            case 'kms':
                                tracker(TRACKER.ENTER_KMS);
                                break;
                            case 'open_platform':
                                tracker(TRACKER.OPEN_PLATFORM_ENTER);
                                break;
                            default:
                                break;
                        }
                    }}
                />
                {hasLink && name && (
                    <Hyperlink
                        compact
                        underline={false}
                        target="_self"
                        className="text"
                        href={url}
                        onClick={get(menuProps, 'handleMenuClick')}
                    >
                        <div className={classNames('menu-icon', defaultMenu)}>
                            <img src={pc_icon_url} />
                        </div>
                        {name}
                    </Hyperlink>
                )}
            </div>
        );
    };

    const getItemInfo = (key?: string) => {
        if (key) {
            return find(corpUserStore.navigationModules, (item) => {
                return item.key === key;
            });
        }
    };

    const filterComponents = (compList?: NAV_COMPONENT[][]) => {
        const resultList: NAV_COMPONENT[][] = [];
        forEach(compList, (comp) => {
            const tempComps: NAV_COMPONENT[] = [];
            forEach(comp, (c) => {
                if (isNavCompShow(c)) {
                    tempComps.push(c);
                }
            });
            if (!isEmpty(tempComps)) {
                resultList.push(tempComps);
            }
        });
        return resultList;
    };

    const rightValue = filterComponents(right);
    const leftValue = filterComponents(left);

    const renderNavLeft = () => {
        if (leftEl) {
            return <div className="navigation-left">{leftEl}</div>;
        }
        if (!isEmpty(leftValue)) {
            return <div className="navigation-left">{createComponents(leftValue)}</div>;
        }
        return null;
    };

    const renderNavRight = () => {
        if (rightEl) {
            return rightEl;
        }
        return (
            !isEmpty(rightValue) && (
                <div className="navigation-right">
                    {rightPlugin}
                    {createComponents(rightValue)}
                </div>
            )
        );
    };

    return (
        <div className={cls} style={style}>
            {renderNavLeft()}
            {navCenter && <div className="navigation-center">{navCenter}</div>}
            {renderNavRight()}
        </div>
    );
};

export default NavigationBar;
